import { z } from "zod";
import {
  EXPERIMENT_ENTITY_PREFIX,
  isValidPrefixedKsuid,
  PROJECT_ENTITY_PREFIX,
  USER_ENTITY_PREFIX,
} from "@/utils/ksuidGenerator";

const prefixedKsuid = (prefix: string, label: string) =>
  z.string().refine((id) => isValidPrefixedKsuid(id, prefix), {
    message: `Invalid ${label} public ID`,
  });

export const AssignExperimentPayloadSchema = z.object({
  experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
  experimentTitle: z.string().min(1).optional(),
  projectPublicId: prefixedKsuid(PROJECT_ENTITY_PREFIX, "project"),
});

export const AssignExperimentResolveSchema = z.object({
  assigneeUserPublicId: prefixedKsuid(USER_ENTITY_PREFIX, "user"),
  dueAt: z.iso.datetime().optional(),
});

export type AssignExperimentPayload = z.infer<typeof AssignExperimentPayloadSchema>;
export type AssignExperimentResolve = z.infer<typeof AssignExperimentResolveSchema>;

/** Tool result shape persisted in thread state and sent to /chat serialization. */
export type AssignExperimentToolResult = AssignExperimentResolve & {
  toolCallId: string;
};

type AssignExperimentEntity = {
  type?: string;
  experimentPublicId?: string;
  experimentTitle?: string;
  projectPublicId?: string;
  toolInvoke?: { toolCallId?: string };
};

export type AssignExperimentArgs = {
  entities?: AssignExperimentEntity[];
  experimentPublicId?: string;
  experimentTitle?: string;
  projectPublicId?: string;
};

/**
 * Parses assign card payload from assistant tool args (flat or entities[] shape).
 */
export function parseAssignExperimentPayload(args: unknown, toolCallId?: string) {
  const direct = AssignExperimentPayloadSchema.safeParse(args);
  if (direct.success) {
    return direct;
  }

  const root = args as AssignExperimentArgs | null | undefined;
  const entities = root?.entities ?? [];
  const entity =
    entities.find(
      (item) =>
        item.type === "assign_experiment" &&
        (!toolCallId || item.toolInvoke?.toolCallId === toolCallId),
    ) ?? entities.find((item) => item.type === "assign_experiment");

  if (!entity) {
    return direct;
  }

  return AssignExperimentPayloadSchema.safeParse({
    experimentPublicId: entity.experimentPublicId,
    experimentTitle: entity.experimentTitle,
    projectPublicId: entity.projectPublicId,
  });
}
